// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only.

// D-CRX-89 — what an edit or a retire reaches, before it happens, and what it does not. Shared by both warnings.
//
// Round 2 (owner 2026-10-28, per-patient pinning): a change reaches the DRUGS — i.e. NEW enrollments — only. Every
// enrolled patient keeps the version they were enrolled with, so their unsent enrollment forms and upcoming visits
// are listed under "Not affected" along with documents already out, Plans of Treatment and past infusion records.

import { useState, type ReactNode } from 'react'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import AppButton from '../../../components/ui/AppButton'
import type { ProtocolUsage } from '../../../api/carerxProtocols'

const COLLAPSE_AT = 5
const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`

function Section({ title, count, empty, testId, children }: {
  title: string; count: number; empty: string; testId: string; children?: ReactNode
}) {
  return (
    <Box data-testid={testId} sx={{ borderTop: '1px solid var(--color-border)', pt: 1 }}>
      <Box sx={{ fontSize: 13, fontWeight: 700 }}>{title} <Box component="span" sx={{ color: 'var(--color-text-soft)' }}>({count})</Box></Box>
      {count === 0 ? <Box sx={{ fontSize: 12.5, color: 'var(--color-text-soft)' }}>{empty}</Box> : children}
    </Box>
  )
}

/** A list that shows the first few and a "Show all" toggle when long. */
function Collapsible({ rows, label }: { rows: string[]; label: string }) {
  const [all, setAll] = useState(false)
  const shown = all ? rows : rows.slice(0, COLLAPSE_AT)
  return (
    <Box sx={{ fontSize: 12.5 }}>
      {shown.map((r, i) => <Box key={i} sx={{ pl: 1 }}>• {r}</Box>)}
      {rows.length > COLLAPSE_AT && (
        <AppButton size="small" onClick={() => setAll(a => !a)}>{all ? 'Show fewer' : `Show all ${rows.length} ${label}`}</AppButton>
      )}
    </Box>
  )
}

export default function ProtocolImpact({ usage, action }: { usage: ProtocolUsage; action: 'edit' | 'retire' }) {
  const na = usage.not_affected
  return (
    <Stack spacing={1.25} data-testid="protocol-impact">
      <Section testId="impact-drugs"
        title={action === 'edit' ? 'Drugs — new enrollments will use the new version' : 'Drugs — new enrollments will no longer get it'}
        count={usage.drugs.length} empty="No drug uses it.">
        <Collapsible rows={usage.drugs.map(d => d.name)} label="drugs" />
      </Section>
      <Section testId="impact-not-affected" title="Not affected" count={na.enrolled_patients}
        empty="No enrolled patient is on this protocol.">
        <Stack spacing={0.25} sx={{ fontSize: 12.5 }}>
          <Box data-testid="impact-enrolled"><b>{plural(na.enrolled_patients, 'enrolled patient keeps', 'enrolled patients keep')} their version</b>
            {' '}— including {plural(na.unsent_forms, 'enrollment form', 'enrollment forms')} not sent yet and{' '}
            {plural(na.upcoming_visits, 'upcoming visit', 'upcoming visits')}. Staff can move a patient on purpose from the enrollment door.</Box>
        </Stack>
      </Section>
      <Box data-testid="impact-documents" sx={{ fontSize: 12.5, borderTop: '1px solid var(--color-border)', pt: 1 }}>
        <b>Also not affected:</b> {plural(na.sent_or_signed_forms, 'sent or signed enrollment form', 'sent or signed enrollment forms')},{' '}
        {plural(na.existing_pots, 'existing Plan of Treatment', 'existing Plans of Treatment')} (pinned),{' '}
        {plural(na.past_infusion_records, 'past infusion record', 'past infusion records')}. They keep the version they were made with.
      </Box>
    </Stack>
  )
}
